/**
 * JoinCompanyPage — landing do link magic.
 * URL: /join-company/{token}
 *
 * Fluxo:
 *   1. Verifica sessão. Se sem sessão → redireciona a `/login?next=/join-company/{token}`
 *   2. Chama `POST /api/companies/join/{token}` para aceitar
 *   3. Sucesso → toast + redirect a `/empresa` (marshall) ou `/dashboard` (member)
 *   4. Erros: convite expirado, email não corresponde, já usado → toast explicativo
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Building2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = { bg: '#2B2A25', card: '#1A1613', ink: '#F2EBDD', inkDim: '#8A7A63', brand: '#E87A2A', green: '#3FA34D' };

export default function JoinCompanyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=/join-company/${token}`);
      return;
    }
    (async () => {
      try {
        const res = await axios.post(
          `${API}/companies/join/${token}`, {},
          { withCredentials: true },
        );
        setResult(res.data);
        setStatus('success');
        toast.success('Bem-vindo à empresa!');
        setTimeout(() => {
          const role = res.data?.role;
          navigate(role === 'company_admin' || role === 'company_staff' ? '/empresa' : '/dashboard');
        }, 2000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Erro ao aceitar convite');
        setStatus('error');
      }
    })();
  }, [authLoading, user, token, navigate]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-6"
      style={{ backgroundColor: C.bg }}
      data-testid="join-company-page"
    >
      <div
        className="w-full max-w-md rounded-xl border p-8 text-center"
        style={{ backgroundColor: C.card, borderColor: 'rgba(196,162,117,0.35)' }}
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin" style={{ color: C.brand }} />
            <div className="mt-4 text-sm" style={{ color: C.ink }}>A validar convite…</div>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: C.green }} />
            <div className="mt-4 text-lg font-bold" style={{ color: C.ink }}>
              Bem-vindo à empresa!
            </div>
            <div className="text-sm mt-2" style={{ color: C.inkDim }}>
              Empresa: <b style={{ color: C.brand }}>{result?.company_id}</b>
              {result?.role && <> · Role: <b style={{ color: C.brand }}>{result.role}</b></>}
            </div>
            <div className="mt-4 text-xs" style={{ color: C.inkDim }}>
              A redirecionar…
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <Building2 className="w-14 h-14 mx-auto" style={{ color: C.brand }} />
            <div className="mt-4 text-lg font-bold" style={{ color: C.ink }}>
              Convite não aceite
            </div>
            <div className="text-sm mt-2" style={{ color: C.inkDim }}>
              {error}
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-6 px-4 py-2 rounded-md text-sm font-bold"
              style={{ backgroundColor: C.brand, color: '#fff' }}
              data-testid="join-back-btn"
            >
              Voltar ao dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
